<template>
  <Card class="mb-0 h-full min-h-[300px] gap-2 py-3" data-testid="dashboard-tasks-card">
    <div class="border-b border-gray-200 px-4 pb-2">
      <div class="flex items-center justify-between gap-4">
        <div class="flex items-center gap-2">
          <CardTitle>Tasks</CardTitle>
          <Badge variant="gray">{{ totalIncomplete }}</Badge>
        </div>

        <Button as-child size="sm" variant="card-header-action">
          <router-link to="/tasks">View all tasks</router-link>
        </Button>
      </div>
    </div>

    <CardContent class="px-0">
      <div v-if="tasks.length === 0" class="flex flex-1 items-center justify-center px-6 py-6">
        <div class="flex max-w-sm flex-col items-center text-center">
          <div class="flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-slate-500">
            <CheckCircle2 class="h-5 w-5" />
          </div>
          <p class="mt-4 text-sm font-medium text-slate-900">No tasks assigned</p>
          <p class="mt-1 text-sm text-slate-500">You're all caught up — no open tasks right now.</p>
          <Button as-child size="sm" variant="outline" class="mt-4">
            <router-link to="/tasks">View all tasks</router-link>
          </Button>
        </div>
      </div>

      <router-link
        v-for="task in tasks"
        :key="task.id"
        :to="`/tasks/${task.id}`"
        class="group flex items-start gap-3 border-b border-gray-200 px-4 py-3 transition-colors hover:bg-slate-50 last:border-b-0"
      >
        <div class="min-w-0 flex-1 space-y-1">
          <p class="line-clamp-2 text-sm leading-snug text-gray-900">{{ task.name }}</p>
          <div class="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-500">
            <span v-if="task.context_type === 'Contact' && task.context?.name" class="truncate">
              {{ task.context?.name }}
            </span>
            <UserTaskProductAreaBadge :product-area="task.product_area" />
            <TaskDueDateBadge :task="task" />
          </div>
        </div>

        <ChevronRight
          class="h-3.5 w-3.5 shrink-0 text-gray-400 transition-opacity sm:opacity-0 sm:group-hover:opacity-100"
        />
      </router-link>

      <router-link
        v-if="additionalTaskCount > 0"
        to="/tasks"
        class="block px-4 py-2.5 text-center text-xs font-medium text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-700"
      >
        + {{ additionalTaskCount }} more
      </router-link>
    </CardContent>
  </Card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { UserTaskEntity } from '@/api/entity_types/UserTask';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardTitle } from '@/components/ui/card';
import TaskDueDateBadge from '@/components/user_tasks/TaskDueDateBadge.vue';
import UserTaskProductAreaBadge from '@/components/user_tasks/UserTaskProductAreaBadge.vue';
import { CheckCircle2, ChevronRight } from 'lucide-vue-next';

const props = defineProps<{
  tasks: UserTaskEntity[];
  totalIncomplete: number;
}>();

const additionalTaskCount = computed(() => Math.max(props.totalIncomplete - props.tasks.length, 0));
</script>
